import { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setURLPaths } from "../../app/appInitializeReducer";
import requestLogo from "../../assets/request.svg";
import { HTTP_STATUS } from "../../http/constants/http.status";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import BiButton from "../primitives/buttons/BiButton.primitive";
import Card from "../shared/cards/Card";
import InputField from "../shared/Fields/InputField";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import { getUserName } from "../../util/admin-utils";
import { cloneDeep, filter, find, findKey, isEmpty, lowerCase } from "lodash";
import { RouteConstants } from "../../constants/RouteConstants";
import { AppConstants } from "../../constants/AppConstants";
import { useTheme } from "../../context/themeContext";
import PickList, { PICKLIST_TYPES } from "../shared/Fields/PickList";
import TextAreaField from "../shared/Fields/TextAreaField";
import { getUsers } from "../../http/user-management-services";
import AutoCompleteInput from "../shared/Fields/AutocompleteInput";
import { generateToken, getFormInfo } from "./Util";
import {
  isEmail,
  isPhone,
  validateEmail,
  validatePhone,
} from "../../util/validators";

import { createRequest, listRequestId } from "../../http/ticket-service";
import { setToastData } from "../../app/toastReducer";
import Dialog from "../shared/Dialog";
import { setRequestDetails } from "../../app/requestReducer";
import moment from "moment";
import { StatusField } from "../shared/Fields/StatusField";
import { useCheckMobileScreen } from "../../util/customHooks";
import { Label } from "../Auth/AuthStyles";
import DisclaimerInfo from "./DisclaimerInfo";
import AdverseEventsInfo from "./AdverseEvents";

/**
 * @parentId patient-request
 * @manager Create or View - Patient Request
 * @overview Creating or Viewing a Request
 * <section>
 *   <p>
 *     <b>View Request</b><br/>
 *     Clicking on the request row item on the past requests listing page will take the user to view request details page in view-only mode. Clicking on <b>Back</b> will take the user to past requests listing page. Users can only view the request details and the current status of the request.
 *     <br/><br/>
 *     <b>Create Request</b><br/>
 *     Users can create a request by clicking on the <b>Create Request</b> button on the Past Request Listing page or by clicking <b>Create Request</b> navigation menu item under the <strong>Data Subject Request</strong> menu group of the portal's left sidebar.
 *     Once inside the Create Form, clicking on <b>Cancel</b> will take the user to the request listing page.
 *     <br/><br/>
 *     Users can create a new request by entering <b>Subject's Name</b>, <b>Username</b>, <b>Request type</b> mandatory fields and clicking on <b>Save</b> button on the admin portal toolbar. <b>Additional Message</b> is optional.
 *     <br/><br/>
 *     There are also some conditional fields which get populated based on the selection in <b>Request type</b> field. List of Conditional Fields:
 *     <br/>
 *     <ul>
 *       <li>User Selects <b>"Data Correction"</b>: "Correction Message" Field is rendered which is mandatory.</li>
 *       <li>User Selects <b>"Data Restriction (may disable some services)"</b>: "Restriction Type" Dropdown Field is rendered which is mandatory.</li>
 *     </ul>
 *     <p>
 *       There are two Policies at the end of the form. They indicate Disclaimer and Adverse Event Policy.
 *       <br/><br/>
 *       <b>Disclaimer</b><br/>
 *       Set of information which is localized in different languages used to indicate crucial information regarding the request submission. A user with <b>IT_ADMIN</b> Role can go and upload a New Language Pack for Disclaimer or Update the existing Disclaimer Language Pack. This is done by clicking <b>Customization</b> navigation menu item under the <strong>Settings</strong> menu group of the portal's left sidebar and then clicking on the <strong>Policies</strong> Tab and uploading under the <strong>Disclaimer Policy</strong>.
 *       <br/><br/>
 *       <b>Adverse Event Policy</b><br/>
 *       Set of Information which is localized in different languages (mainly English, Spanish, Japanese, and French) and is only country-specific (mainly United States:us, Argentina:ar, Mexico:mx, Chile:cl, Canada:ca). They provide various contact details for health centers in different countries where users can report any negative side effects. A user with <b>IT_ADMIN</b> Role can go and upload a New Language Pack for Disclaimer or Update the existing Disclaimer Language Pack. This is done by clicking <b>Customization</b> navigation menu item under the <strong>Settings</strong> menu group of the portal's left sidebar and then clicking on the <strong>Policies</strong> Tab and uploading under the <strong>Data Requests Adherence Policy </strong>.
 *     </p>
 *   </p>
 *   <br/>
 *   <p>
 *     <h4>Create/View request Fields</h4>
 *     Create/View request page will display below fields with the following validation constraints.
 *     <br/>
 *     <table>
 *       <tr>
 *         <th>Field Name</th>
 *         <th>Required</th>
 *         <th>Data Type</th>
 *         <th>Field Type</th>
 *         <th>Condition</th>
 *         <th>Editable</th>
 *       </tr>
 *       <tr>
 *         <td>Requested by</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>No</td>
 *       </tr>
 *       <tr>
 *         <td>Subject's Name</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Username</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Request type</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Dropdown</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Additional message (optional)</td>
 *         <td>No</td>
 *         <td>String</td>
 *         <td>TextArea</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Correction Message</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>TextArea</td>
 *         <th>Request type: Data Correction</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Restriction Type</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Dropdown</td>
 *         <th>Request type: Data Restriction (may disable some services)</th>
 *         <td>Yes</td>
 *       </tr>
 *     </table>
 *     <br/>
 *   </p>
 * </section>
 * <section>
 *   <h4>Failure Status Codes</h3>
 *   <p>
 *     This section describes Create/Edit notify template failure status code information.
 *     <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>401</td>
 *         <td>UMS_AUTH_REQUIRED</td>
 *         <td>You must authenticate before you can use the user-management-service.</td>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>NA</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *       <tr>
 *         <td>400</td>
 *         <td>NA</td>
 *         <td>Bad Request</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h4>Dependent System settings, Platform services & Role Permission</h3>
 *   <p>This section describes the list of dependent system settings & platform services required for the functioning of Create/View request page.</p>
 *   <h5>System Settings</h4>
 *   <p>Table lists all the dependent system setting(s) defined in the configuration service with either global/organization scope.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Key</th>
 *       <th>Type</th>
 *       <th>Value</th>
 *       <th>Scope</th>
 *     </tr>
 *     <tr>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <h5>Platform Service(s)</h4>
 *   <p>Table lists all the dependent platform service(s) with specific version(s) for Create/View request page.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Asset Management Service</td>
 *       <td>1.3.0</td>
 *     </tr>
 *     <tr>
 *       <td>Ticket Service</td>
 *       <td>V2</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.2.2</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 * </section>
 */

const CreateNewRequest = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const theme = useTheme().theme;

  const userProfile = useAppSelector(
    (state: any) => state.userManagement.userProfileInfo
  );
  const reqDetails: any = useAppSelector(
    (state) => state?.request?.requestDetails
  );

  const [isEdit, setIsEdit] = useState(false);

  const isMobileScreen = useCheckMobileScreen();

  const [isLoading, setIsLoading] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [editInitiated, setEditInitiated] = useState(false);

  const [formSchema, setFormSchema] = useState<any>(null);
  const [fieldsSchema, setFieldSchema] = useState<any>(null);

  const [creatorName, setCreatorName] = useState<any>("");
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);

  const [formError, setFormError] = useState<any>(null);

  const [type, setType] = useState("");
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showAdverseEvents, setShowAdverseEvents] = useState<boolean>(false);
  const [adverseExist, setAdverseExist] = useState<boolean>(false);

  const country =
    localStorage.getItem(AppConstants.LOCAL_STORAGE_COUNTRY_KEY) ||
    localStorage.getItem(AppConstants.LOCAL_STORAGE_LOCALE_KEY) ||
    "";

  const STATUS_BUILDER = {
    solved: {
      style: {
        background: "#87929D",
        color: "white",
        fontWeight: "600",
        borderRadius: "4px",
      },
      description: "",
      display: t("T_SOLVED"),
    },
    new: {
      style: {
        background: "#FFB648",
        color: "#703B15",
        fontWeight: "600",
        borderRadius: "4px",
      },
      description: "",
      display: t("T_NEW"),
    },
    open: {
      style: {
        background: "#E34F32",
        color: "white",
        fontWeight: "600",
        borderRadius: "4px",
      },
      description: "",
      display: t("T_OPEN"),
    },
    pending: {
      style: {
        background: "#3091EC",
        color: "white",
        fontWeight: "600",
        borderRadius: "4px",
      },
      description: "",
      display: t("T_PENDING"),
    },
  };

  const setBreadCrumps = () => {
    dispatch(
      setURLPaths([
        {
          key: !params?.id
            ? RouteConstants.ROUTE_GDPR_CREATE
            : RouteConstants.ROUTE_GDPR_VIEW.replace(":id", params?.id || ""),
          label: `${t("T_PATIENT_REQUESTS")} > ${
            !params?.id ? t("T_NEW_REQUEST") : t("T_VIEW_REQUEST")
          }`,
        },
      ])
    );
  };

  useEffect(() => {
    if (params?.id) {
      setIsEdit(true);
      setReviewMode(true);
    } else {
      checkAdverseEvents();
    }
    setBreadCrumps();
    return () => {
      dispatch(setRequestDetails(null));
    };
  }, []);

  async function checkAdverseEvents() {
    let flag = sessionStorage.getItem(
      AppConstants.LOCALISATION_NAMESPACE_ADVERSE
    );
    setAdverseExist(flag === "true");
  }

  async function initialize() {
    getFormInfo("")
      .then((result) => {
        const { formSchema, fieldData } = result;
        setFormSchema(formSchema);
        setFieldSchema(fieldData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }
  useEffect(() => {
    if (!isEmpty(userProfile) && isEmpty(formSchema) && isEmpty(fieldsSchema)) {
      setCreatorName(getUserName(userProfile));
      initialize();
    }
  }, [userProfile]);

  useLayoutEffect(() => {
    if (
      location?.pathname === RouteConstants.ROUTE_GDPR_CREATE &&
      !isEmpty(reqDetails)
    ) {
      setEditInitiated(false);
      dispatch(setRequestDetails(null));
      setFieldSchema(null);
      setFormSchema(null);
      setIsLoading(true);
      setBreadCrumps();
      initialize();
      setIsEdit(false);
      setReviewMode(false);
    }
  }, [location]);

  useEffect(() => {
    async function init() {
      try {
        setIsLoading(true);
        let res = await listRequestId(Number(params?.id));
        if (HTTP_STATUS.isSuccess(res?.status)) {
          dispatch(setRequestDetails(res?.data?.result));
          setIsLoading(false);
        }
      } catch (e) {
        setIsLoading(false);
      }
    }
    if (params?.id && isEmpty(reqDetails)) {
      init();
    }
    if (
      params?.id &&
      !isEmpty(reqDetails) &&
      !isEmpty(fieldsSchema) &&
      !editInitiated
    ) {
      const clonedSchema = cloneDeep(fieldsSchema);
      reqDetails?.fields?.forEach((item: any) => {
        const { id, value } = item;
        if (clonedSchema?.[id]) {
          clonedSchema[id].value = value;
        }
      });
      setFieldSchema(clonedSchema);
      setEditInitiated(true);
    }

    if (
      !params?.id &&
      !isEmpty(fieldsSchema) &&
      !editInitiated &&
      isEmpty(reqDetails)
    ) {
      presetData();
    }
  }, [reqDetails, userProfile, fieldsSchema]);

  const presetData = () => {
    const clonedSchema = cloneDeep(fieldsSchema);
    let indexUsername = findKey(clonedSchema, { title: "Username" });
    let indexName = findKey(clonedSchema, { title: "Subject Name" });
    let indexClientName = findKey(clonedSchema, { title: "Client Name" });
    if (indexUsername) {
      clonedSchema[indexUsername].value =
        userProfile?.mail || userProfile?.mobilePhone;
    }
    if (indexName) {
      clonedSchema[indexName].value = getUserName(userProfile);
    }
    if (indexClientName) {
      clonedSchema[indexClientName].value = localStorage.getItem("clientName")
        ? localStorage.getItem("clientName")
        : "";
    }
    setFieldSchema(clonedSchema);
    setEditInitiated(true);
  };

  const conditionalField = (id: number) => {
    let isConditional = formSchema?.end_user_conditions.findIndex((item: any) =>
      item.child_fields.some((field: any) => field.id === id)
    );

    if (isConditional !== -1) {
      let flag = false;
      if (formSchema?.end_user_conditions[isConditional]) {
        const { parent_field_id, value } =
          formSchema?.end_user_conditions[isConditional];

        if (fieldsSchema?.[parent_field_id]?.value === value) {
          flag = true;
        }
      }
      return flag;
    }
    return true;
  };

  const validateFormData = () => {
    let flag = true;
    const usernameField: any = !isEmpty(fieldsSchema)
      ? Object.values(fieldsSchema)?.find(
          (field: any) => field?.title_in_portal === "Username"
        )
      : {};

    if (!isEmpty(usernameField) && usernameField?.value?.trim().length === 0) {
      flag = false;
    }

    formSchema?.ticket_field_ids?.forEach((id: number) => {
      if (conditionalField(id)) {
        if (!fieldsSchema?.[id]?.value && fieldsSchema?.[id]?.required) {
          flag = false;
          return;
        }
      }
    });
    return flag;
  };

  const generatePayload = () => {
    let payload: any = {
      request: {
        subject: type.length
          ? `${t("T_GDPR")} - ${type}`
          : t("T_GDPR_REQUEST_SUBJECT"),
        // description: "",
        requester: {
          name: creatorName.trim(),
          // email: userProfile?.mail || "",
          // phone: userProfile?.mobilePhone || "",
          requester_id: userProfile?.userId || "",
          clientName: localStorage.getItem("clientName")
            ? localStorage.getItem("clientName")
            : "",
        },
        custom_fields: {},
        comment: {
          type: "comment",
          body: t("T_GDPR_REQUEST_SUBJECT"),
        },
        ticket_form_id: formSchema?.id,
      },
    };
    Object.entries(fieldsSchema).forEach(([key, val]: any) => {
      const { value, type, id } = val;
      if (
        !AppConstants.ZENDESK_SYSTEM_FIELDS.includes(type) &&
        conditionalField(id) &&
        value
      ) {
        payload.request.custom_fields[id] = value.trim();
      }
    });
    return payload;
  };

  const createCall = async () => {
    setConfirmDialog(false);
    setIsLoading(true);
    const response: any = await createRequest(generatePayload());
    const success: boolean = HTTP_STATUS.isSuccess(response?.status);
    dispatch(
      setToastData({
        isToastActive: true,
        toastMessage: success
          ? t("T_TICKET_CREATED_SUCCESS")
          : t("T_TICKET_CREATION_FAILURE"),
        toastType: success
          ? AppConstants.TOAST_TYPE_SUCCESS
          : AppConstants.TOAST_TYPE_ERROR,
      })
    );
    setIsLoading(false);

    if (success) {
      navigate(RouteConstants.ROUTE_GDPR_LIST);
    }
  };

  const handleSave = async () => {
    const errors = validateFormData();
    const isValid = true; //Object.values(errors).every((error) => error === "");

    if (isValid) {
      setConfirmDialog(true);
      // setReviewMode(true);
    }
    // if (reviewMode) {
    //   setConfirmDialog(true);
    // }
  };

  const getCancelButtonText = () => {
    if (reviewMode) {
      return t("T_BACK");
    }
    return t("T_CANCEL");
  };

  const handleChange = (key: number, value: any) => {
    let clonedObj = cloneDeep(fieldsSchema);
    if (clonedObj[key].value === value || isEmpty(value)) {
      clonedObj[key].value = "";
    } else {
      clonedObj[key].value = value;
    }

    if (clonedObj[key]?.title_in_portal === "Request Type") {
      let index = clonedObj[key]?.custom_field_options?.findIndex(
        (item: any) => item.value === value
      );
      setType(clonedObj[key]?.custom_field_options[index]?.name);
    }
    setFieldSchema(clonedObj);
  };

  const handleOnCancel = () => {
    setIsEdit(false);
  };

  const REQUEST_OPTIONS = [
    { key: "request", value: t("T_NEW_PATIENT_REQUEST_TYPE_REQUEST") },
    { key: "export", value: t("T_NEW_PATIENT_REQUEST_TYPE_EXPORT") },
    {
      key: "rectification",
      value: t("T_NEW_PATIENT_REQUEST_TYPE_RECTIFICATION"),
    },
    { key: "deletion", value: t("T_NEW_PATIENT_REQUEST_TYPE_DELETION") },
    { key: "restriction", value: t("T_NEW_PATIENT_REQUEST_TYPE_RESTRICTION") },
  ];

  const fetchUserData = async (name: string = "") => {
    let trimmedName = name.trim();
    let apiFilter = trimmedName
      ? `[{"field":"mail","operation":"co","value":"${trimmedName}"}]`
      : "";

    if (isEmpty(trimmedName)) {
      return [];
    }
    let response = await getUsers(
      apiFilter,
      AppConstants.DEFAULT_PAGE,
      AppConstants.DEFAULT_PAGE_SIZE,
      ""
    );

    const { status, data: usersResponse } = response;
    if (HTTP_STATUS.isSuccess(status)) {
      return usersResponse.data;
    } else {
      return [];
    }
  };

  const clearError = (key: string) => {
    let formErrorClone = { ...formError };
    if (formErrorClone?.[key]) {
      delete formErrorClone[key];
      setFormError(formErrorClone);
    }
  };

  const getFieldStyle = (title: any) => {
    if (title.toLowerCase() === "client name") {
      return { display: "none" };
    }
    return {};
  };

  const enableFieldLabel = (title: any) => {
    if (title.toLowerCase() === "client name") {
      return true;
    }
    return false;
  };

  const renderField = (fieldObj: any = {}) => {
    const {
      value,
      id,
      title,
      title_in_portal,
      error,
      required,
      type,
      description,
      custom_field_options = [],
    } = fieldObj;
    if (conditionalField(id)) {
      switch (type) {
        case "textarea":
          return (
            <div className="mb-6 flex w-full flex-wrap">
              <div className="flex w-full flex-col">
                <TextAreaField
                  fieldKey={id["value"]}
                  type={"text"}
                  id={id}
                  label={t(title_in_portal)}
                  placeholder={t(`${title_in_portal}_HELPER`)}
                  required={required && !isEdit}
                  value={value}
                  onChange={(val: any) => {
                    handleChange(id, val);
                  }}
                  error={error}
                  asterixFirst
                  rows={3}
                  disabled={reviewMode}
                  maxLength={AppConstants.MAX_LENGTH_IMPORT_DESCRIPTION}
                  className={`!resize-y rounded border px-3 py-2`}
                ></TextAreaField>
              </div>
            </div>
          );
        // case "status":
        //   return (
        //     <>
        //       {isEdit ? (
        //         <div className="mb-6 flex w-full flex-col">
        //           <InputField
        //             type={"text"}
        //             id={id}
        //             label={title_in_portal}
        //             placeholder={description}
        //             value={capitalize(reqDetails?.status) || ""}
        //             onChange={(val: any) => {}}
        //             disabled={true}
        //             error={error}
        //             asterixFirst
        //             required={required && !isEdit}
        //             min={1}
        //             max={1024}
        //           />
        //         </div>
        //       ) : (
        //         ""
        //       )}
        //     </>
        //   );

        case "text":
          return (
            <div className="mb-6 flex w-full flex-col">
              {false ? ( //title.includes("Email")
                <AutoCompleteInput
                  value={value}
                  onChange={(val: any) => {
                    if (formError?.[title_in_portal]) {
                      clearError(title_in_portal);
                    }
                    handleChange(id, val);
                  }}
                  disabled={reviewMode}
                  onBlur={(event: any) => {
                    if (validateEmail(event.target.value)) {
                      clearError(title_in_portal);
                    } else {
                      setFormError({
                        ...formError,
                        [title_in_portal]: t("T_VALIDATION_ERR_EMAIL_MSG"),
                      });
                    }
                  }}
                  error={formError?.[title_in_portal]}
                  dataKey={"mail"}
                  placeholder={t(`${title_in_portal}_HELPER`)}
                  fetchData={fetchUserData}
                  required={required && !isEdit}
                  label={t(title_in_portal)}
                />
              ) : (
                <InputField
                  type={"text"}
                  id={id}
                  label={t(title_in_portal)}
                  disableLabel={enableFieldLabel(title)}
                  placeholder={t(`${title_in_portal}_HELPER`) || description}
                  value={value}
                  onChange={(val: any) => {
                    if (formError?.[title_in_portal]) {
                      clearError(title_in_portal);
                    }
                    handleChange(id, val);
                  }}
                  disabled={reviewMode}
                  error={formError?.[title_in_portal]}
                  asterixFirst
                  required={
                    (required && !isEdit) || title_in_portal === "Username"
                  }
                  min={1}
                  style={getFieldStyle(title)}
                  max={1024}
                />
              )}
            </div>
          );

        case "tagger":
          return (
            <div className="mb-6 flex w-full flex-wrap">
              <div className="flex w-full flex-col">
                <PickList
                  fieldKey={"value"}
                  fieldValues={["name"]}
                  required={required && !isEdit}
                  placeholder={t(`${title_in_portal}_HELPER`)}
                  label={t(title_in_portal)}
                  selectedItems={
                    fieldsSchema[id]?.value
                      ? filter(custom_field_options, { value }).map(
                          (item: any) => {
                            return { ...item, name: t(item.name) };
                          }
                        )
                      : []
                  }
                  onSelectedChange={(items: any) => {
                    handleChange(id, items[0]?.value);
                  }}
                  items={custom_field_options.map((item: any) => {
                    return { ...item, name: t(item.name) };
                  })}
                  disabled={reviewMode}
                  readOnly={false}
                  type={PICKLIST_TYPES.SINGLE}
                />
              </div>
            </div>
          );
        default:
          return <div></div>;
      }
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      {showInfo ? (
        <DisclaimerInfo
          visibility={showInfo}
          setVisibility={(boolValue: boolean) => {
            setShowInfo(boolValue);
          }}
        ></DisclaimerInfo>
      ) : (
        ""
      )}

      {showAdverseEvents ? (
        <AdverseEventsInfo
          visibility={showAdverseEvents}
          setVisibility={(boolValue: boolean) => {
            setShowAdverseEvents(boolValue);
          }}
          country={country}
        ></AdverseEventsInfo>
      ) : (
        ""
      )}
      <Dialog
        isActive={confirmDialog}
        setIsActive={() => {
          setConfirmDialog(!confirmDialog);
        }}
        title={t("T_CONFIRM")}
        text={
          <div className="pt-3 text-base font-medium">
            {t("T_CONFIRM_DETAILS_SUB")}
          </div>
        }
        primaryButtonText={t("T_YES")}
        primaryOnClick={createCall}
        tertiaryButtonText={t("T_BACK")}
        tertiaryOnClick={() => {
          setConfirmDialog(false);
        }}
        primaryButtonWidth="8.25rem"
      />
      <MenuButtonsPortal>
        <BiButton
          className={"tertiary mr-2 border py-1.5"}
          type="button"
          onClick={() => {
            if (!reviewMode || (reviewMode && isEdit)) {
              navigate(RouteConstants.ROUTE_GDPR_LIST);
            } else {
              setReviewMode(false);
            }
          }}
        >
          {getCancelButtonText()}
        </BiButton>
        {!isEdit ? (
          <BiButton
            className="bg-primary text-white"
            type="button"
            onClick={handleSave}
            disabled={!validateFormData() || !isEmpty(formError)}
          >
            {reviewMode
              ? t("T_NEW_PATIENT_REQUEST_CREATE_REQUEST")
              : t("T_SAVE")}
          </BiButton>
        ) : (
          ""
        )}
      </MenuButtonsPortal>
      <div
        className="flex max-h-screen w-full flex-1 flex-col justify-start overflow-y-scroll  bg-gray-100 px-2 py-3"
        style={useTheme().theme.bgColorStyleForLoggedInPage}
      >
        <div className="flex">
          <div className={`flex ${params.id ? "w-2/3" : "w-full"} md:w-3/4`}>
            <div className="p-0 md:p-2"></div>
            <Card
              title={
                isEdit ? t("T_VIEW_REQUEST") : t("T_NEW_PATIENT_REQUEST_HEADER")
              }
              icon={requestLogo}
              alt="file-code-icon"
              className="w-full"
              height={""}
              width={""}
            >
              {!params.id && (
                <div className="mb-6 text-xs font-medium md:text-base">
                  {t("T_CREATE_REQUEST_HELPER")}
                </div>
              )}
              {!isEmpty(formSchema) && !isEmpty(fieldsSchema) && (
                <div className="flex w-full flex-col flex-wrap">
                  <div className="mb-6 flex w-full flex-col">
                    <InputField
                      type={"text"}
                      id={"creator-name"}
                      label={t("Creator Name")}
                      placeholder={""}
                      value={creatorName}
                      asterixFirst
                      required={!isEdit}
                      error=""
                      disabled
                      onChange={(e: any) => {}}
                      min={1}
                      max={1024}
                    />
                  </div>
                  {formSchema?.ticket_field_ids.map((fieldId: number) => {
                    return renderField(fieldsSchema[fieldId]);
                  })}
                </div>
              )}
              {!isLoading && !params?.id ? (
                <div className="self-center !text-xs font-medium md:!text-base">
                  {t("T_CREATE_REQUEST_PROMPT_P1")}{" "}
                  <Label
                    onClick={() => {
                      setShowInfo(true);
                    }}
                    className="cursor-pointer self-center !text-xs md:!text-base"
                    style={{ color: theme.secondary1 }}
                  >
                    {t("T_DISCLAIMER")}
                  </Label>{" "}
                  {AppConstants.SUPPORTED_ADVERSE_COUNTRIES.includes(country) &&
                  adverseExist
                    ? t("T_AND")
                    : ""}{" "}
                  {AppConstants.SUPPORTED_ADVERSE_COUNTRIES.includes(country) &&
                  adverseExist ? (
                    <Label
                      className="cursor-pointer self-center !text-xs md:!text-base"
                      style={{ color: theme.secondary1 }}
                      onClick={() => {
                        setShowAdverseEvents(true);
                      }}
                    >
                      {t("T_ADVERSE_EVENT")}
                    </Label>
                  ) : (
                    ""
                  )}{" "}
                  {t("T_CREATE_REQUEST_PROMPT_P2")}
                </div>
              ) : (
                ""
              )}
            </Card>
          </div>
          {params?.id && (
            <div className="ml-5 flex w-1/3 flex-col overflow-hidden md:w-1/4">
              <div className="my-7">
                <div className="my-2.5">
                  <StatusField
                    type={"text"}
                    id={"status"}
                    label={t("T_STATUS")}
                    placeholder={t("T_STATUS")}
                    required={false}
                    value={reqDetails?.status}
                    error={""}
                    disabled
                    layout="100"
                    metadata={STATUS_BUILDER}
                  />
                </div>
                <div className="my-2.5">
                  <div>{t("T_CREATED_ON")}</div>
                  <div>
                    {moment(reqDetails?.created_at).format(
                      isMobileScreen
                        ? AppConstants.MOBILE_DATA_FORMAT
                        : AppConstants.DEFAULT_DATE_FORMAT
                    )}
                  </div>
                </div>
                <div className="my-2.5">
                  <div>{t("T_UPDATED_ON")}</div>
                  <div>
                    {moment(reqDetails?.updated_at).format(
                      isMobileScreen
                        ? AppConstants.MOBILE_DATA_FORMAT
                        : AppConstants.DEFAULT_DATE_FORMAT
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </LoaderContainer>
  );
};

export default CreateNewRequest;
